import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Grid, Paper, Typography, alpha } from '@mui/material';
import {
  Restaurant, ShoppingCart, MenuBook, Inventory, Storefront, LocalOffer,
  Category, Extension, Schedule, ReceiptLong, Payments, AccountBalanceWallet,
  ArrowForward,
} from '@mui/icons-material';
import { selectEnabledModules } from '../../store/slices/tenantSlice';
import DashboardSection from '../dashboard/DashboardSection';

const VENDOR_ACTIONS = [
  { label: 'Live Food Orders', path: '/vendor/food/live-orders', icon: Restaurant, module: 'food', color: '#ea580c' },
  { label: 'Live Grocery Orders', path: '/vendor/grocery/live-orders', icon: ShoppingCart, module: 'grocery', color: '#00695C' },
  { label: 'Food Order History', path: '/vendor/food/order-history', icon: ReceiptLong, module: 'food', color: '#b45309' },
  { label: 'Grocery History', path: '/vendor/grocery/order-history', icon: ReceiptLong, module: 'grocery', color: '#00796b' },
  { label: 'Restaurant Profile', path: '/food/restaurants', icon: Storefront, module: 'food', color: '#059669' },
  { label: 'Menu Catalog', path: '/food/menu', icon: MenuBook, module: 'food', color: '#d97706' },
  { label: 'Add Menu Item', path: '/food/items/new', icon: MenuBook, module: 'food', color: '#ca8a04' },
  { label: 'Addon Groups', path: '/food/addon-groups', icon: Extension, module: 'food', color: '#7c3aed' },
  { label: 'Categories', path: '/hubs/delivery/food-delivery/masters', icon: Category, module: 'food', color: '#2563eb' },
  { label: 'Offers & Coupons', path: '/vendor/promos', icon: LocalOffer, module: 'food', feature: 'promo', color: '#db2777' },
  { label: 'Opening Hours', path: '/food/restaurants?tab=hours', icon: Schedule, module: 'food', color: '#0891b2' },
  { label: 'Grocery Stores', path: '/grocery/stores', icon: Storefront, module: 'grocery', color: '#0d9488' },
  { label: 'Products / Inventory', path: '/grocery/products', icon: Inventory, module: 'grocery', color: '#16a34a' },
  { label: 'Add Grocery Product', path: '/grocery/products/new', icon: Inventory, module: 'grocery', color: '#15803d' },
  { label: 'Settlements', path: '/settlements', icon: Payments, color: '#0284c7' },
  { label: 'Wallet', path: '/wallet', icon: AccountBalanceWallet, module: 'wallet', feature: 'wallet', color: '#6366f1' },
];

export default function VendorHubQuickActions() {
  const navigate = useNavigate();
  const enabledModules = useSelector(selectEnabledModules);
  const enabledFeatures = useSelector((s: { tenant: { enabledFeatures?: string[] } }) => s.tenant.enabledFeatures || []);

  const actions = useMemo(() => VENDOR_ACTIONS.filter((a) => {
    if (a.module && !enabledModules.includes(a.module)) return false;
    if (a.feature && !enabledFeatures.includes(a.feature)) return false;
    return true;
  }), [enabledModules, enabledFeatures]);

  if (!actions.length) return null;

  return (
    <DashboardSection title="Vendor Hub" subtitle="All store operations — food, grocery, menu, inventory, and payouts" accent="#059669">
      <Grid container spacing={1.5}>
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <Grid item xs={6} sm={4} md={3} key={action.path}>
              <Paper
                elevation={0}
                onClick={() => navigate(action.path)}
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  border: 1,
                  borderColor: 'divider',
                  cursor: 'pointer',
                  height: '100%',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    borderColor: action.color,
                    boxShadow: `0 8px 24px ${alpha(action.color, 0.15)}`,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                  <Box>
                    <Box sx={{ color: action.color, display: 'flex', mb: 0.75 }}><Icon fontSize="small" /></Box>
                    <Typography variant="body2" fontWeight={700} lineHeight={1.35}>{action.label}</Typography>
                  </Box>
                  <ArrowForward sx={{ fontSize: 16, color: 'text.disabled', mt: 0.25 }} />
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </DashboardSection>
  );
}
